export type FramePixels = {
  width: number;
  height: number;
  /** RGBA, row-major (ImageData layout). */
  data: Uint8ClampedArray;
};

/** A cell counts as changed when any channel moves by more than this —
 *  above JPEG/scaling noise, below any drawn overlay. */
const CHANNEL_DIFF_THRESHOLD = 48;
/** At most this share of cells may change. Deliberately tiny: calling two
 *  frames identical drops the user's choice, so a small watermark in a
 *  corner must still count as a difference. */
const MAX_CHANGED_SHARE = 0.002;

/**
 * Whether the preview still and the raw video frame look the same, compared
 * on equally-sized downscaled copies (e.g. 36×64). True means nothing the
 * preview draws on top (layout, subtitles, layers, watermark) is visible at
 * that moment, so offering both as a choice would be pointless.
 */
export function framesLookIdentical(a: FramePixels, b: FramePixels): boolean {
  if (a.width !== b.width || a.height !== b.height) return false;
  const cells = a.width * a.height;
  if (cells === 0 || a.data.length < cells * 4 || b.data.length < cells * 4) return false;
  const maxChanged = Math.floor(cells * MAX_CHANGED_SHARE);
  let changed = 0;
  for (let i = 0; i < cells * 4; i += 4) {
    const diff = Math.max(
      Math.abs(a.data[i] - b.data[i]),
      Math.abs(a.data[i + 1] - b.data[i + 1]),
      Math.abs(a.data[i + 2] - b.data[i + 2]),
    );
    if (diff > CHANNEL_DIFF_THRESHOLD) {
      changed += 1;
      if (changed > maxChanged) return false;
    }
  }
  return true;
}
